// src/pages/subscription/components/CurrentSubscription.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Calendar, 
  CreditCard, 
  RefreshCw, 
  TrendingUp,
  AlertCircle,
  XCircle,
  CheckCircle,
  Loader,
  X,
  AlertTriangle
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscriptionStatus } from '../../../hooks/useSubscriptionStatus';
import { 
  getMySubscription, 
  getMyUsage,
  cancelSubscription,
  reactivateSubscription
} from '../../../services/api/subscription';
import { 
  formatSubscriptionStatus, 
  getTrialDaysRemaining,
  formatUsage,
  formatPrice,
  getDaysRemaining,
  formatUsagePercentage,
  getEffectivePrice, 
  hasCustomPricing 
} from '../../../utils/subscriptionUtils';
import { formatDate } from '../../../utils/dateUtils';
import styles from './CurrentSubscription.module.css';

const CurrentSubscription = () => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isExpired, isCancelled, daysRemaining, shouldShowWarning } = useSubscriptionStatus();

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  const currency = user?.business?.currency || 'LKR';
  const effectivePrice = getEffectivePrice(subscription);
  const hasCustomPrice = hasCustomPricing(subscription);
  const basePrice = subscription?.plan?.price || 0;

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setIsLoading(true);
      const [subResponse, usageResponse] = await Promise.all([
        getMySubscription(),
        getMyUsage()
      ]);

      setSubscription(subResponse.subscription);
      setUsage(usageResponse.usage);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      if (err.response?.status !== 404) {
        showError(t('subscription.loadSubscriptionFailed'), {
          title: t('common.error')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    try {
      setIsCancelling(true);
      await cancelSubscription(cancelReason);
      success(t('subscription.cancelSuccess'), {
        title: t('common.success')
      });
      setShowCancelModal(false);
      setCancelReason('');
      await loadSubscriptionData();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      showError(err.response?.data?.message || t('subscription.cancelFailed'), {
        title: t('common.error')
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsReactivating(true);
      await reactivateSubscription();
      success(t('subscription.reactivateSuccess'), {
        title: t('common.success')
      });
      await loadSubscriptionData();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      showError(err.response?.data?.message || t('subscription.reactivateFailed'), {
        title: t('common.error')
      });
    } finally {
      setIsReactivating(false);
    }
  };

  const handleRenewSubscription = () => {
    navigate('/subscription/plans');
  };

  if (isLoading) {
    return (
      <div className={styles.currentSubscription}>
        <div className={styles.loadingState}>
          <Loader className={styles.spinner} size={40} />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className={styles.currentSubscription}>
        <div className={styles.emptyState}>
          <Package size={48} className={styles.emptyIcon} />
          <h3>{t('subscription.noSubscription')}</h3>
          <p>{t('subscription.chooseAPlan')}</p>
          <button 
            className={styles.primaryButton}
            onClick={() => navigate('/subscription/plans')}
          >
            {t('subscription.viewPlans')}
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = formatSubscriptionStatus(subscription);
  const trialDays = getTrialDaysRemaining(subscription);
  const daysUntilBilling = getDaysRemaining(subscription.nextBillingDate);

  const resourceTypes = ['products', 'shops', 'ecomOrders', 'users', 'warehouses'];

  return (
    <div className={styles.currentSubscription}>
      {/* Expiration Warning (7 days or less) */}
      {shouldShowWarning && daysRemaining > 0 && (
        <div className={styles.expirationWarning}>
          <AlertTriangle size={20} />
          <div className={styles.warningContent}>
            <span className={styles.warningTitle}>
              {t('subscription.subscriptionExpiringSoon', { days: daysRemaining })}
            </span>
            <span className={styles.warningText}>
              {t('subscription.renewBeforeExpiration')}
            </span>
          </div>
          <button 
            className={styles.warningButton}
            onClick={handleRenewSubscription}
          >
            <RefreshCw size={16} />
            {t('subscription.renewNow')}
          </button>
        </div>
      )}

      {/* Subscription Overview Card */}
      <div className={styles.overviewCard}>
        <div className={styles.cardHeader}>
          <h2>{t('subscription.currentSubscription')}</h2>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.planInfo}>
            <div className={styles.planName}>
              <Package size={24} />
              <span>{subscription.plan?.name || 'N/A'}</span>
            </div>
            <div 
              className={styles.statusBadge}
              style={{
                backgroundColor: statusInfo.color === 'green' ? '#10b98120' : 
                                 statusInfo.color === 'blue' ? '#3b82f620' :
                                 statusInfo.color === 'red' ? '#ef444420' :
                                 statusInfo.color === 'orange' ? '#f59e0b20' : '#6b728020',
                borderColor: statusInfo.color === 'green' ? '#10b981' : 
                            statusInfo.color === 'blue' ? '#3b82f6' :
                            statusInfo.color === 'red' ? '#ef4444' :
                            statusInfo.color === 'orange' ? '#f59e0b' : '#6b7280',
                color: statusInfo.color === 'green' ? '#065f46' : 
                       statusInfo.color === 'blue' ? '#1e40af' :
                       statusInfo.color === 'red' ? '#991b1b' :
                       statusInfo.color === 'orange' ? '#92400e' : '#374151'
              }}
            >
              {statusInfo.label}
            </div>
          </div>

          {subscription.status === 'trial' && trialDays > 0 && (
            <div className={styles.trialAlert}>
              <AlertCircle size={20} />
              <span>
                {t('subscription.trialEndsIn', { days: trialDays })}
              </span>
            </div>
          )}

          {subscription.status === 'cancelled' && subscription.cancelledAt && (
            <div className={styles.cancelledAlert}>
              <XCircle size={20} />
              <span>
                {t('subscription.cancelledOn')} {formatDate(subscription.cancelledAt)}
              </span>
            </div>
          )}

          {/* Expired Alert with Renew Button */}
          {isExpired && (
            <div className={styles.expiredAlert}>
              <XCircle size={20} />
              <div className={styles.expiredContent}>
                <span>{t('subscription.subscriptionHasExpired')}</span>
                <button 
                  className={styles.expiredRenewButton}
                  onClick={handleRenewSubscription}
                >
                  <RefreshCw size={16} />
                  {t('subscription.renewSubscription')}
                </button>
              </div>
            </div>
          )}

          <div className={styles.subscriptionDetails}>
            <div className={styles.detailRow}>
              <div className={styles.detailItem}>
                <Calendar size={16} />
                <div>
                  <div className={styles.detailLabel}>{t('subscription.startDate')}</div>
                  <div className={styles.detailValue}>{formatDate(subscription.startDate)}</div>
                </div>
              </div>

              {subscription.nextBillingDate && subscription.status !== 'cancelled' && subscription.status !== 'expired' && (
                <div className={styles.detailItem}>
                  <Calendar size={16} />
                  <div>
                    <div className={styles.detailLabel}>{t('subscription.nextBillingDate')}</div>
                    <div className={styles.detailValue}>
                      {formatDate(subscription.nextBillingDate)}
                      {daysUntilBilling > 0 && (
                        <span className={styles.daysRemaining}>
                          ({daysUntilBilling} {t('subscription.days')})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.detailRow}>
              <div className={styles.detailItem}>
                <CreditCard size={16} />
                <div>
                  <div className={styles.detailLabel}>{t('subscription.billingCycle')}</div>
                  <div className={styles.detailValue}>
                    {t(`subscription.${subscription.billingCycle}`)}
                  </div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <CreditCard size={16} />
                <div>
                  <div className={styles.detailLabel}>{t('subscription.planPrice')}</div>
                  <div className={styles.detailValue}>
                    {hasCustomPrice && effectivePrice < basePrice && (
                      <>
                        <span className={styles.originalPriceSmall}>
                          {formatPrice(basePrice, currency)}
                        </span>
                        {' '}
                      </>
                    )}
                    {formatPrice(effectivePrice, currency)}
                    {hasCustomPrice && effectivePrice < basePrice && (
                      <span className={styles.customBadgeSmall}>
                        {t('subscription.customRate')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.actionButtons}>
            {subscription.status === 'cancelled' || subscription.status === 'expired' ? (
              <button 
                className={styles.primaryButton}
                onClick={handleRenewSubscription}
                disabled={isReactivating}
              >
                {isReactivating ? (
                  <>
                    <Loader className={styles.spinner} size={18} />
                    {t('subscription.processing')}
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    {t('subscription.renewSubscription')}
                  </>
                )}
              </button>
            ) : (
              <>
                <button 
                  className={styles.primaryButton}
                  onClick={() => navigate('/subscription/plans')}
                >
                  <TrendingUp size={18} />
                  {t('subscription.upgrade')}
                </button>
                <button 
                  className={styles.secondaryButton}
                  onClick={() => setShowCancelModal(true)}
                >
                  <XCircle size={18} />
                  {t('subscription.cancel')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Usage Statistics Section */}
      {usage && (
        <div className={styles.usageSection}>
          <div className={styles.sectionHeader}>
            <h3>{t('subscription.usageStatistics')}</h3>
          </div>

          <div className={styles.usageGrid}>
            {resourceTypes.map(resourceType => {
              const resourceUsage = usage[resourceType];
              if (!resourceUsage) return null;

              const { color, percentage } = formatUsagePercentage(
                resourceUsage.usage, 
                resourceUsage.limit
              );

              return (
                <div key={resourceType} className={styles.usageCard}>
                  <div className={styles.usageHeader}>
                    <div className={styles.usageTitle}>
                      {t(`subscription.${resourceType}`)}
                    </div>
                    {resourceUsage.period !== 'total' && (
                      <div className={styles.usagePeriod}>
                        {t(`subscription.per${resourceUsage.period.charAt(0).toUpperCase() + resourceUsage.period.slice(1)}`)}
                      </div>
                    )}
                  </div>

                  <div className={styles.usageStats}>
                    <div className={styles.usageNumbers}>
                      <span className={styles.currentUsage}>
                        {resourceUsage.usage}
                      </span>
                      <span className={styles.usageSeparator}>/</span>
                      <span className={styles.usageLimit}>
                        {resourceUsage.isUnlimited 
                          ? t('subscription.unlimited') 
                          : resourceUsage.limit}
                      </span>
                    </div>
                    {!resourceUsage.isUnlimited && (
                      <div className={styles.usagePercentage} style={{ color }}>
                        {percentage}%
                      </div>
                    )}
                  </div>

                  {!resourceUsage.isUnlimited && (
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill}
                        style={{ 
                          width: `${Math.min(percentage, 100)}%`,
                          backgroundColor: color
                        }}
                      />
                    </div>
                  )}

                  {!resourceUsage.isUnlimited && (
                    <div className={styles.remainingCount}>
                      {resourceUsage.remaining} {t('subscription.remaining')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className={styles.modalOverlay} onClick={() => !isCancelling && setShowCancelModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{t('subscription.confirmCancellation')}</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p>{t('subscription.cancelMessage')}</p>

              <div className={styles.formGroup}>
                <label>{t('subscription.cancellationReason')}</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={isCancelling}
                  className={styles.select}
                >
                  <option value="">{t('common.selectOption')}</option>
                  <option value="too_expensive">{t('subscription.tooExpensive')}</option>
                  <option value="missing_features">{t('subscription.missingFeatures')}</option>
                  <option value="not_using_enough">{t('subscription.notUsingEnough')}</option>
                  <option value="switching_to_competitor">{t('subscription.switchingToCompetitor')}</option>
                  <option value="other">{t('subscription.other')}</option>
                </select>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelButton}
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                {t('common.close')}
              </button>
              <button 
                className={styles.dangerButton}
                onClick={handleCancelSubscription}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <>
                    <Loader className={styles.spinner} size={18} />
                    {t('subscription.processing')}
                  </>
                ) : (
                  <>
                    <XCircle size={18} />
                    {t('subscription.confirmCancel')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentSubscription;